import React, { useState, useEffect } from "react";
import { PostCategory, LanguageCode } from "../../types/post";
import { useLanguage } from "../../providers/languageProvider";

interface MultiLanguageCategoryFormProps {
  category?: PostCategory | null;
  isOpen: boolean;
  onClose: () => void;
  onSubmit: (
    translations: {
      name: Record<LanguageCode, string>;
      description?: Record<LanguageCode, string>;
    },
    groupKey?: string,
    icon?: string,
    color?: string
  ) => Promise<void>;
}

export const MultiLanguageCategoryForm: React.FC<
  MultiLanguageCategoryFormProps
> = ({ category, isOpen, onClose, onSubmit }) => {
  const { availableLanguages } = useLanguage();
  const [categoryGroupKey, setCategoryGroupKey] = useState("");
  const [categoryIcon, setCategoryIcon] = useState("");
  const [categoryColor, setCategoryColor] = useState("");

  // State for translations
  const [nameTranslations, setNameTranslations] = useState<
    Record<LanguageCode, string>
  >({
    en: "",
    pl: "",
    uk: "",
  });
  const [descriptionTranslations, setDescriptionTranslations] = useState<
    Record<LanguageCode, string>
  >({
    en: "",
    pl: "",
    uk: "",
  });

  // Update form when category changes
  useEffect(() => {
    if (category) {
      setCategoryGroupKey(category.group_key || "");
      setCategoryIcon(category.icon || "");
      setCategoryColor(category.color || "");

      // Load translations if available
      if (category.translations) {
        setNameTranslations({
          en: category.translations.name?.en || "",
          pl: category.translations.name?.pl || "",
          uk: category.translations.name?.uk || "",
        });
        setDescriptionTranslations({
          en: category.translations.description?.en || "",
          pl: category.translations.description?.pl || "",
          uk: category.translations.description?.uk || "",
        });
      } else {
        // Fallback to current name/description if no translations
        setNameTranslations({
          en: category.name || "",
          pl: "",
          uk: "",
        });
        setDescriptionTranslations({
          en: category.description || "",
          pl: "",
          uk: "",
        });
      }
    } else {
      // Reset for new category
      setCategoryGroupKey("");
      setCategoryIcon("");
      setCategoryColor("");
      setNameTranslations({ en: "", pl: "", uk: "" });
      setDescriptionTranslations({ en: "", pl: "", uk: "" });
    }
  }, [category]);

  const handleSubmit = async (e: React.FormEvent) => {
    e.preventDefault();

    // Validate that at least Polish name is provided
    if (!nameTranslations.pl.trim()) {
      alert("Nazwa w języku polskim jest wymagana");
      return;
    }

    try {
      await onSubmit(
        {
          name: nameTranslations,
          description: descriptionTranslations,
        },
        categoryGroupKey.trim() || undefined,
        categoryIcon.trim() || undefined,
        categoryColor.trim() || undefined
      );
      handleClose();
    } catch (error) {
      console.error("Error saving category:", error);
      alert("Nie udało się zapisać kategorii");
    }
  };

  const handleClose = () => {
    setCategoryGroupKey("");
    setCategoryIcon("");
    setCategoryColor("");
    setNameTranslations({ en: "", pl: "", uk: "" });
    setDescriptionTranslations({ en: "", pl: "", uk: "" });
    onClose();
  };

  const updateNameTranslation = (language: LanguageCode, value: string) => {
    setNameTranslations((prev) => ({ ...prev, [language]: value }));
  };

  const updateDescriptionTranslation = (
    language: LanguageCode,
    value: string
  ) => {
    setDescriptionTranslations((prev) => ({ ...prev, [language]: value }));
  };

  if (!isOpen) return null;

  return (
    <div className="fixed inset-0 bg-black bg-opacity-50 flex items-center justify-center z-[1000]">
      <div className="bg-white p-6 rounded-lg w-[600px] max-w-[90vw] shadow-xl max-h-[90vh] overflow-y-auto">
        <h3 className="m-0 mb-4 text-lg font-semibold">
          {category ? "Edytuj Kategorię" : "Utwórz Nową Kategorię"}
        </h3>

        <form onSubmit={handleSubmit}>
          {/* Name Translations */}
          <div className="mb-6">
            <label className="block mb-3 font-medium text-base">
              Nazwa kategorii:
            </label>
            {availableLanguages.map((lang) => (
              <div key={`name-${lang.code}`} className="mb-3">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  {lang.flag} {lang.name}{" "}
                  {lang.code === "pl" && (
                    <span className="text-red-500">*</span>
                  )}
                </label>
                <input
                  type="text"
                  value={nameTranslations[lang.code]}
                  onChange={(e) =>
                    updateNameTranslation(lang.code, e.target.value)
                  }
                  required={lang.code === "pl"}
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:outline-none"
                  placeholder={`Wprowadź nazwę kategorii w języku ${lang.name.toLowerCase()}`}
                />
              </div>
            ))}
          </div>

          {/* Description Translations */}
          <div className="mb-6">
            <label className="block mb-3 font-medium text-base">
              Opis kategorii (opcjonalnie):
            </label>
            {availableLanguages.map((lang) => (
              <div key={`desc-${lang.code}`} className="mb-3">
                <label className="block mb-1 text-sm font-medium text-gray-600">
                  {lang.flag} {lang.name}
                </label>
                <textarea
                  value={descriptionTranslations[lang.code]}
                  onChange={(e) =>
                    updateDescriptionTranslation(lang.code, e.target.value)
                  }
                  className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm resize-y focus:border-blue-500 focus:outline-none"
                  placeholder={`Opis kategorii w języku ${lang.name.toLowerCase()}`}
                  rows={2}
                  maxLength={500}
                />
              </div>
            ))}
          </div>

          {/* Other Fields */}
          <div className="mb-4">
            <label className="block mb-1 text-sm font-medium text-gray-600">
              Klucz Grupowy Kategorii (opcjonalny):
            </label>
            <input
              type="text"
              value={categoryGroupKey}
              onChange={(e) => setCategoryGroupKey(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:outline-none"
              placeholder="np. technologia, lifestyle"
              pattern="[a-z0-9_-]*"
              title="Tylko małe litery, cyfry, podkreślenia i myślniki"
            />
            <p className="text-xs text-gray-500 mt-1">
              Klucz grupowy służy do grupowania kategorii. Może być taki sam dla
              wielu kategorii.
            </p>
          </div>

          <div className="mb-4">
            <label className="block mb-2 font-medium">
              Ikona (opcjonalnie):
            </label>
            <input
              type="text"
              value={categoryIcon}
              onChange={(e) => setCategoryIcon(e.target.value)}
              className="w-full px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:outline-none"
              placeholder="np. 📚, 💻, 🏠 lub nazwa ikony"
            />
            <p className="text-xs text-gray-500 mt-1">
              Emoji lub nazwa ikony do wyświetlania przy kategorii.
            </p>
          </div>

          <div className="mb-6">
            <label className="block mb-2 font-medium">
              Kolor (opcjonalnie):
            </label>
            <div className="flex gap-2">
              <input
                type="color"
                value={categoryColor || "#3b82f6"}
                onChange={(e) => setCategoryColor(e.target.value)}
                className="w-12 h-10 border border-gray-200 rounded-md cursor-pointer"
              />
              <input
                type="text"
                value={categoryColor}
                onChange={(e) => setCategoryColor(e.target.value)}
                className="flex-1 px-3 py-2 border border-gray-200 rounded-md text-sm focus:border-blue-500 focus:outline-none"
                placeholder="#3b82f6"
                pattern="^#[0-9A-Fa-f]{6}$"
                title="Kod koloru w formacie hex (np. #3b82f6)"
              />
            </div>
            <p className="text-xs text-gray-500 mt-1">
              Kolor kategorii w formacie hex (np. #3b82f6).
            </p>
          </div>

          <div className="flex gap-3 justify-end">
            <button
              type="button"
              onClick={handleClose}
              className="px-4 py-2 bg-gray-50 text-gray-600 border-0 rounded-md cursor-pointer hover:bg-gray-100 transition-colors"
            >
              Anuluj
            </button>
            <button
              type="submit"
              className="px-4 py-2 bg-blue-500 text-white border-0 rounded-md cursor-pointer hover:bg-blue-600 transition-colors"
            >
              {category ? "Zapisz zmiany" : "Utwórz kategorię"}
            </button>
          </div>
        </form>
      </div>
    </div>
  );
};
